import {CommonModule, NgFor} from "@angular/common";
import {HttpErrorResponse} from "@angular/common/http";
import {Component, inject, Input, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {finalize, Subject, takeUntil} from 'rxjs';
import {CustomerService} from "../../../../core/services/customer.service";
import {CustomerBillingDetails} from "../../../../core/types/customer.types";
import {EntityResponse} from "../../../../core/types/types";
import {CreditCardImage, getAcceptedCreditCards} from "../../../../core/utils/credit-card.utils";
import {PageConfigurationService} from '../../../../core/services/page-configuration.service';
import {PageConfigurationResponse} from '../../../../core/types/page-configuration.types';
import {BillingDetails, MerchantPlatformSettings, PaymentTokenData} from "../../../../core/types/payment.types";
import {ModalService} from "../../../../core/services/modal.service";
import {
    PaymentCardInputWrapperComponent
} from '../../../payment/payment-card-input-wrapper/payment-card-input-wrapper.component';
import {ErrorService} from "../../../../core/services/error.service";
import {FormValueSyncDirective} from "../../../../core/directives/form-value-sync.directive";
import countries from '../../../../core/data/countries';
import {FormMaxLengths} from "../../../../core/validators/form-max-lengths";
import {strictEmailValidator} from "../../../../core/validators/strict-email.validator";

import {FormValueSyncRegistry} from "../../../../core/directives/form-value-sync.registry";

@Component({
    selector: "app-add-new-credit-card",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, NgFor, PaymentCardInputWrapperComponent, FormValueSyncDirective],
    templateUrl: "./add-new-credit-card.component.html",
    styleUrl: "./add-new-credit-card.component.scss",
})
export class AddNewCreditCardComponent implements OnInit, OnDestroy {
    private _formValueSync = inject(FormValueSyncRegistry);
    @Input() onAuthSuccess?: () => void;
    @ViewChild(PaymentCardInputWrapperComponent) paymentCardWrapper?: PaymentCardInputWrapperComponent;

    private _errorService = inject(ErrorService);

    form!: FormGroup;
    showFormErrors: boolean = false;
    showLoading: boolean = false;
    showSuccess: boolean = false;

    acceptedCreditCards: CreditCardImage[] = [];
    merchantPlatform?: MerchantPlatformSettings;

    customerId: number = 0;

    existingLastFourDigits?: string;

    billingDetailsFailedToLoad: boolean = false;
    componentInitializedFully: boolean = false;

    validCountriesList = countries;

    readonly maxLengths = FormMaxLengths;

    private _customerService = inject(CustomerService);
    private _configurationService = inject(PageConfigurationService);
    private _modalService = inject(ModalService);
    private _destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.fetchAcceptedCreditCards();
        this.fetchCustomerBillingDetails();
    }

    fetchAcceptedCreditCards(): void {
        this._configurationService.pageConfiguration$
            .pipe(takeUntil(this._destroy$))
            .subscribe(
                (response: PageConfigurationResponse | null) => {
                    if (response && response.success) {
                        this.acceptedCreditCards = getAcceptedCreditCards(response.data!.paymentConfiguration.acceptedCreditCards);
                    }
                }
            );
    }

    fetchCustomerBillingDetails(): void {
        this._customerService.getBillingDetails()
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (response: EntityResponse<CustomerBillingDetails>) => {
                    this.createForm();
                    this.form.patchValue({
                        ...response.data,
                        firstName: response.data.firstName?.trim(),
                        lastName: response.data.lastName?.trim(),
                        country: response.data.country?.slice(0, 2) || 'US'
                    });
                    this.customerId = response.data!.id;
                    this.merchantPlatform = response.data!.merchantPlatformCredentials;
                    this.existingLastFourDigits = response.data!.lastFourDigits;
                },
                error: (error: HttpErrorResponse) => {
                    const errorMessage = error.error?.title
                        || error.error?.message
                        || error.message
                        || 'Unable to load billing details. Please try again later.';
                    this.billingDetailsFailedToLoad = true;
                    this._errorService.setError(errorMessage);
                },
                complete: () => {
                    this.componentInitializedFully = true;
                }
            });
    }

    createForm(): void {
        this.form = new FormGroup({
            firstName: new FormControl(null, [Validators.required, Validators.maxLength(FormMaxLengths.Name)]),
            lastName: new FormControl(null, [Validators.maxLength(FormMaxLengths.Name)]),
            email: new FormControl(null, [Validators.required, strictEmailValidator(), Validators.maxLength(FormMaxLengths.Email)]),
            phone: new FormControl(null, [Validators.required, Validators.maxLength(FormMaxLengths.Phone)]),
            address: new FormControl(null, [Validators.required, Validators.maxLength(FormMaxLengths.Address)]),
            city: new FormControl(null, [Validators.required, Validators.maxLength(FormMaxLengths.City)]),
            state: new FormControl(null, [Validators.required, Validators.maxLength(2), Validators.minLength(2)]),
            postalCode: new FormControl(null, [Validators.required, Validators.maxLength(FormMaxLengths.PostalCode)]),
            country: new FormControl(null, [Validators.maxLength(2)])
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    get formControls() {
        return {
            firstName: this.form.get("firstName") as FormControl,
            lastName: this.form.get("lastName") as FormControl,
            email: this.form.get("email") as FormControl,
            phone: this.form.get("phone") as FormControl,
            address: this.form.get("address") as FormControl,
            city: this.form.get("city") as FormControl,
            state: this.form.get("state") as FormControl,
            postalCode: this.form.get("postalCode") as FormControl,
            country: this.form.get("country") as FormControl
        }
    }

    get headerTitle(): string {
        return this.showSuccess ? 'Success!' : 'New Credit Card';
    }

    get headerSubtitle(): string {
        if (this.showSuccess) {
            return '';
        }
        return this.existingLastFourDigits
            ? `We already have a credit card on file ending in <b>${this.existingLastFourDigits}</b>.<br/>`
            : 'Please enter your credit card information below.';
    }

    async onSubmit(): Promise<void> {
        this._formValueSync.syncAll();

        if (this.form.invalid) {
            this.showFormErrors = true;
            return;
        }

        if (!this.paymentCardWrapper || !this.paymentCardWrapper.validate()) {
            this.showFormErrors = true;
            this._modalService.showError('Invalid Card', 'Please complete all card information');
            return;
        }

        this.showLoading = true;

        try {
            const formValue = this.form.value;

            const billingDetails: BillingDetails = {
                customerId: this.customerId,
                fullName: `${formValue.firstName.trim()} ${formValue.lastName?.trim() ?? ''}`.trim(),
                email: formValue.email,
                phone: formValue.phone,
                address: formValue.address,
                city: formValue.city,
                state: formValue.state,
                postalCode: formValue.postalCode,
                country: formValue.country || 'US'
            };

            const paymentToken: PaymentTokenData = await this.paymentCardWrapper.createPaymentMethod(billingDetails);

            paymentToken.firstName = formValue.firstName.trim();
            paymentToken.lastName = formValue.lastName?.trim() ?? '';
            paymentToken.address = formValue.address;
            paymentToken.city = formValue.city;
            paymentToken.state = formValue.state;
            paymentToken.postalCode = formValue.postalCode;
            paymentToken.country = formValue.country || 'US';

            this._customerService.addNewCreditCard(null, paymentToken, true)
                .pipe(
                    finalize(() => {
                        this.showLoading = false;
                    })
                )
                .subscribe({
                    next: () => {
                        this.showSuccess = true;
                    },
                    error: (error: HttpErrorResponse) => {
                        this.onHttpError(error);
                    }
                });

        } catch (error: any) {
            this.showLoading = false;
            this._modalService.showError('Payment Error', error.message || 'Failed to process payment');
        }
    }

    onHttpError(error: HttpErrorResponse): void {
        this._modalService.showError('Error', error.error?.title);
    }

    onStateInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const upper = input.value.toUpperCase();
        if (upper !== input.value) {
            input.value = upper;
            this.formControls.state.setValue(upper);
        }
    }
}

